import { useContext, useEffect, useState } from 'react';
import { AdminContext } from '../contexts/AdminContext';
import { OptionsType, rolePermission } from '../interfaces/ApplicationInterfaces';
import './EditComponent.css';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import { ApplicationData, Claims, ExternalEnumRecord, claimEnabledInContext, claimsListToSchema, makeExternalEnumRecord } from '../interfaces/APIInterface';
import MenuItem from '@mui/material/MenuItem';
import { COSMOS_API, POST_ENDPOINT } from '../api/API';
import { ApplicationClaimField } from './ApplicationClaimField';
import { AppEnvSetting, isNullUndefEmpty } from '../ts/common';

interface ApplicationEditProps {
  appInformation: Record<string, string>;
  appExternalEnums: Record<string, Record<string, ExternalEnumRecord>>;
  setAppExternalEnums: React.Dispatch<React.SetStateAction<Record<string, Record<string, ExternalEnumRecord>>>>;
  forOrg?: string;
  selectedApp: string;
  editUserApplication: (data: Record<string, string>, appId: string) => void;
  newApp?: boolean;
  updateApp: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  resetApp: () => void;
  claims: Array<Claims>
}

export const ApplicationEdit = (props: ApplicationEditProps) => {
  var { adminCredentials, getToken } = useContext(AdminContext);
  var output: JSX.Element = <></>;
  const [applicationName, setApplicationName] = useState<string>(
    adminCredentials.allApplications[props.selectedApp!]?.name
  );
  const [noEdit, setNoEdit] = useState(
    props.newApp === undefined ? true : false
  );
  const [loadingAppConfig, setLoadingAppConfig] = useState<boolean>(false);
  const [appConfig, setAppConfig] = useState<ApplicationData>();

  useEffect(() => {
    if (props.selectedApp && props.selectedApp !== '' && props.claims.some(clm => clm.externalEnumeration != null) && (!Object.keys(props.appExternalEnums).includes(props.selectedApp))) {
      let selectedAppRef = props.selectedApp;
      COSMOS_API.POST({ id: selectedAppRef, orgId: props.forOrg ?? null },
        POST_ENDPOINT.GetApplicationExternalEnumerations,
        getToken()
      ).then((res) => {
        props.setAppExternalEnums({
          ...props.appExternalEnums,
          [selectedAppRef]: makeExternalEnumRecord(res.respBody)
        })
      })
    }
  }, [props.forOrg, props.selectedApp])

  useEffect(() => {
    if (props.selectedApp !== undefined && props.selectedApp !== '') {
      setLoadingAppConfig(true);
      setApplicationName(adminCredentials.allApplications[props.selectedApp!]?.name);
      getAppConfig();
    }

    if (!props.newApp) {
      setNoEdit(true);
    }
  }, [props.selectedApp]);

  const getAppConfig = async () => {
    var appData: ApplicationData = (await COSMOS_API.POST(
      { id: props.selectedApp },
      POST_ENDPOINT.GetApplicationData,
      getToken()
    )).respBody;

    if(appData !== null){
      setAppConfig(appData);
    }

    setLoadingAppConfig(false);
  }

  const appClaimsValid = (appsClaims: Claims[] | undefined, appsData: Record<string, string>) => {
    if (appsClaims) {
      return !(appsClaims.filter(aClaim => aClaim.required).some(aClaim => isNullUndefEmpty(appsData?.[aClaim.id])));
    }
    return true;
  }

  const changeValue = (k: string, v: string) => {
    var temp = { ...props.appInformation };
    temp[k] = v;
    props.updateApp({ ...temp });
  };

  const handleSave = () => {
    props.editUserApplication(props.appInformation, props.selectedApp!);
    setNoEdit(true);
  };

  const provisionStatusDropdown = (): JSX.Element => {
    return (
      <TableRow key={`userProvisionStatus`}>
        <TableCell>
          <Autocomplete
            id="provisionApp"
            sx={{ width: 300 }}
            disabled={noEdit || props.newApp || AppEnvSetting.disableAppProvisioning || appConfig?.adminsCannotSetProvisioningStatus}
            value={props.appInformation.userProvisionStatus}
            options={appConfig?.requiresAdminApproval || props.appInformation.userProvisionStatus === 'Pending' ? ['Approved', 'Pending', 'Rejected'] : ['Approved', 'Rejected']}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label={`userProvisionStatus`} />
            )}
            onChange={(_: any, value: string | null) => {
              if (value === null) return;
              changeValue('userProvisionStatus', value);
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const resetApplication = () => {
    props.resetApp();
    setNoEdit(true);
  };

  const toggles = () => {
    return (
      <>
        {!props.newApp &&
        (adminCredentials.isOrganizationAdmin || adminCredentials.adminApplications.find(
          (value: { id: string; name: string; role: string }) =>
            value.id === props.selectedApp &&
            rolePermission(
              'editApplicationData',
              value.role,
              adminCredentials.roleSpecificPermissions,
              adminCredentials.isSuperAdmin
            )
        ) !== undefined) ? (
          noEdit ? (
            <Button onClick={() => setNoEdit(false)}>Edit Application</Button>
          ) : (
            <Stack direction={'row'} spacing={4}>
              <Button disabled={!appClaimsValid(props.claims, props.appInformation)} onClick={handleSave}>Save Changes</Button>
              <Button onClick={resetApplication}>Discard Changes</Button>
            </Stack>
          )
        ) : null}
      </>
    );
  };


    output = (
      <TableContainer
        component={Paper}
        className={
          props.newApp
            ? 'table-wrap new-app-popup'
            : 'table-wrap in-page-apptable'
        }
        style={{
          "overflowY": "auto"
        }}
      >
        <Table className="application-table">
          <TableHead>
            <TableRow>
              <TableCell>Application specific data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {provisionStatusDropdown()}
            {props.claims.filter(cl => claimEnabledInContext(cl, claimsListToSchema(props.claims), (cl.externalEnumeration != null) ? (props.appExternalEnums?.[props.selectedApp]?.[cl.id]?.values ?? []) : cl.possibleValues, props.appInformation)).map((claim: Claims) => {
              return (
                <TableRow key={`${props.selectedApp ?? ''}-${claim.id}`}>
                  <TableCell>
                    <ApplicationClaimField
                      claim={claim}
                      claimValue={props.appInformation[claim.id]}
                      setClaimValue={(appId, cid, cvalue) => {
                        changeValue(cid, cvalue);
                      }}
                      appId={props.selectedApp}
                      appExternalEnums={props.appExternalEnums}
                      disabled={noEdit}
                      claimContext={props.appInformation}
                      claimSchema={claimsListToSchema(props.claims)}
                        />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {toggles()}
      </TableContainer>
    );
  return <>{output}</>;
};
